// Page-to-shell channel. Pages navigate and read the role without editing
// main.ts or changing their render signatures. The hash carries the route
// only; prefilled params stay in memory for one takeNavParams read.

import type {
  AccountPath,
  CompanyHealth,
  CompanySlug,
  RouteName,
  StaffRole,
} from "../types.js";
import type { ShellRoute } from "../nav-registry.js";

export type NavView = "journal" | "accounts" | "trial";

/**
 * One-shot prefilled values. Absent keys are omitted, never set to undefined.
 *
 * `account` and `accountPrefix` are LEDGER PATHS, not words: the receiving page
 * uses them to filter the register, never to label anything. They keep the
 * `AccountPath` brand for that reason — a page that wants to show one to a
 * person goes through `ui/display-words.ts`, and the brand guard can tell the
 * difference (rd13 L4: the Statement checks card links to this account's
 * entries for the month).
 */
export interface NavParams {
  q?: string;
  account?: AccountPath;
  accountPrefix?: AccountPath;
  period?: string;
  source?: string;
  view?: NavView;
  /**
   * rd15 A10: a report's own range, so a drilled figure opens the journal for
   * the months the number covers rather than for one month. Both or neither,
   * like the route's own rule.
   */
  from?: string;
  to?: string;
  /**
   * rd15 A11: the accounts a cash-flow line bucketed. The receiving page reads
   * them as a filter, never as words.
   */
  accounts?: AccountPath[];
  /**
   * rd15 A12: a location, already folded to its current spelling by the report.
   * It is a stored string the ledger matches, never words to print.
   */
  location?: string;
  /** rd15 A2: the rule a booking names, so Rules can open on it. */
  rule?: number;
}

export interface NavBusCall {
  company?: CompanySlug;
  params?: NavParams;
}

export interface FleetHealthSnapshot {
  companies: CompanyHealth[];
}

interface NavBusRegistration {
  navigate: (route: ShellRoute, opts?: NavBusCall) => void;
  currentRole: () => StaffRole | null;
  /**
   * R8-05 (round 2): a Check now press the person was watching has finished.
   * The shell re-reads what its badge and alert banner speak for. Optional so
   * a bare registration (page tests) needs no handler.
   */
  checkSettled?: () => void;
}

let registration: NavBusRegistration | null = null;
let health: FleetHealthSnapshot | null = null;
const healthListeners = new Set<() => void>();
const staged = new Map<ShellRoute, NavParams>();

export function registerNavBus(next: NavBusRegistration): void {
  registration = next;
}

/**
 * R8-05 (round 2): Check now is mounted by five pages, not by the shell, so a
 * page tells the shell through this seam when a press it watched has ended.
 * A no-op when nothing is mounted.
 */
export function notifyCheckSettled(): void {
  registration?.checkSettled?.();
}

export function publishHealth(snapshot: FleetHealthSnapshot | null): void {
  health = snapshot;
  for (const listener of [...healthListeners]) listener();
}

/**
 * Repaint when the shell takes a new fleet-health reading (rd r13 L3.6). Home
 * showed "Connected, last checked 6 minutes ago" after Check now succeeded,
 * because it painted the reading once and nothing told it the reading had
 * changed. The shell publishes every read here; a page re-reads the fact and
 * repaints the one line that speaks for it. Returns the unsubscribe.
 */
export function subscribeHealth(listener: () => void): () => void {
  healthListeners.add(listener);
  return () => {
    healthListeners.delete(listener);
  };
}

/** Latest fleet-health payload the shell already fetched. Null until the first success. */
export function lastHealth(): FleetHealthSnapshot | null {
  return health;
}

export function currentRole(): StaffRole | null {
  return registration?.currentRole() ?? null;
}

export function navigateTo(route: RouteName | "system", params?: NavParams): void {
  const cleaned = cleanParams(params);
  if (cleaned) staged.set(route, cleaned);
  const call: NavBusCall = {};
  if (cleaned) call.params = cleaned;
  registration?.navigate(route, call);
}

/** Read and clear prefilled values for this route. A second call returns null. */
export function takeNavParams(route: RouteName | "system"): NavParams | null {
  const value = staged.get(route) ?? null;
  staged.delete(route);
  return value;
}

/** Test seam: drop staged params and the health snapshot between mounts. */
export function resetNavBusMemory(): void {
  staged.clear();
  health = null;
  healthListeners.clear();
}

function cleanParams(params: NavParams | undefined): NavParams | null {
  if (params === undefined) return null;
  const out: NavParams = {};
  if (params.q !== undefined) out.q = params.q;
  if (params.account !== undefined) out.account = params.account;
  if (params.accountPrefix !== undefined) out.accountPrefix = params.accountPrefix;
  if (params.period !== undefined) out.period = params.period;
  if (params.source !== undefined) out.source = params.source;
  if (params.view !== undefined) out.view = params.view;
  if (params.from !== undefined) out.from = params.from;
  if (params.to !== undefined) out.to = params.to;
  if (params.accounts !== undefined && params.accounts.length > 0) {
    out.accounts = params.accounts;
  }
  if (params.location !== undefined) out.location = params.location;
  if (params.rule !== undefined) out.rule = params.rule;
  return Object.keys(out).length > 0 ? out : null;
}
